import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { authApi } from '@/services/api/auth';
import type { User } from '@supabase/supabase-js';

interface HeaderProps {
  user: User | null;
}

const NAV_LINKS = [
  { label: 'Dashboard', to: '/' },
  { label: 'Team', to: '/team-members' },
  { label: 'Sprints', to: '/sprints' },
] as const;

const truncateEmail = (email: string, maxLen = 24) =>
  email.length > maxLen ? email.slice(0, maxLen) + '…' : email;

export const Header = ({ user }: HeaderProps) => {
  const { pathname } = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  const isActive = (to: string) =>
    to === '/' ? pathname === '/' : pathname.startsWith(to);

  const handleSignOut = async () => {
    await authApi.signOut();
  };

  // Dropdown: close on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  // Dropdown: close on Esc
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDropdownOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [dropdownOpen]);

  // Drawer: focus trap + Esc
  useEffect(() => {
    if (!drawerOpen) return;
    closeButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDrawerOpen(false);
        hamburgerRef.current?.focus();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusable = drawerRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [drawerOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 h-16 z-50 bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between">
        {/* Brand */}
        <Link
          to="/"
          className="text-lg font-bold text-gray-900 hover:text-primary-600 flex-shrink-0"
        >
          PlanArc
        </Link>

        {/* Desktop nav */}
        <nav aria-label="Main navigation" className="hidden md:flex items-center gap-6">
          {NAV_LINKS.map(({ label, to }) => (
            <Link
              key={to}
              to={to}
              aria-current={isActive(to) ? 'page' : undefined}
              className={`text-sm px-1 pb-0.5 ${
                isActive(to)
                  ? 'font-semibold border-b-2 border-primary-600 text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {label}
            </Link>
          ))}
        </nav>

        {/* Right side: user dropdown (desktop) + hamburger (mobile) */}
        <div className="flex items-center gap-2">
          {/* User dropdown — desktop */}
          <div ref={containerRef} className="relative hidden md:block">
            <button
              ref={triggerRef}
              onClick={() => setDropdownOpen(o => !o)}
              aria-expanded={dropdownOpen}
              aria-haspopup="menu"
              className="flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900"
            >
              {truncateEmail(user?.email ?? '')}
              <ChevronDownIcon className="w-4 h-4" aria-hidden="true" />
            </button>

            {dropdownOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full mt-1 w-56 bg-white shadow-lg ring-1 ring-black ring-opacity-5 rounded-md py-1 z-50"
              >
                <div className="px-4 py-2 text-sm text-gray-900 border-b border-gray-100">
                  {user?.email}
                </div>
                <button
                  role="menuitem"
                  onClick={handleSignOut}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>

          {/* Mobile: user chevron (shows email truncated + chevron, no dropdown) */}
          <div className="md:hidden flex items-center gap-1 text-sm text-gray-700">
            {truncateEmail(user?.email ?? '')}
            <ChevronDownIcon className="w-4 h-4" aria-hidden="true" />
          </div>

          {/* Hamburger — mobile only */}
          <button
            ref={hamburgerRef}
            onClick={() => setDrawerOpen(o => !o)}
            aria-label={drawerOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={drawerOpen}
            className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900"
          >
            <Bars3Icon className="w-6 h-6" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/30 z-40 md:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          {/* Drawer panel */}
          <div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            className="fixed top-0 left-0 h-full w-64 bg-white z-50 shadow-xl flex flex-col md:hidden"
          >
            {/* Close button */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <span className="text-sm font-semibold text-gray-900">PlanArc</span>
              <button
                ref={closeButtonRef}
                onClick={() => setDrawerOpen(false)}
                aria-label="Close navigation menu"
                className="p-1 rounded-md text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>

            {/* Nav links */}
            <nav className="flex-1 px-4 py-4 space-y-1">
              {NAV_LINKS.map(({ label, to }) => (
                <Link
                  key={to}
                  to={to}
                  aria-current={isActive(to) ? 'page' : undefined}
                  onClick={() => setDrawerOpen(false)}
                  className={`block px-3 py-2 rounded-md text-sm ${
                    isActive(to)
                      ? 'font-semibold bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  {label}
                </Link>
              ))}
            </nav>

            {/* User section */}
            <div className="border-t border-gray-100 px-4 py-4 space-y-3">
              <p className="text-sm text-gray-900 truncate">{user?.email}</p>
              <button
                onClick={handleSignOut}
                className="w-full text-left text-sm text-gray-600 hover:text-gray-900"
              >
                Sign out
              </button>
            </div>
          </div>
        </>
      )}
    </header>
  );
};
